/*
 * DSD Governance Registry - p11dz147
 * Governance-only authority contract for platform, universe, galaxy, event,
 * story group, and chat/conversation layers. Defines who can govern what,
 * what actions require higher authority, what platform overrides always apply,
 * and how future admin/moderation actions must be audited. Does not enable
 * Firebase reads, Firebase writes, realtime listeners, enforcement, moderation
 * execution, admin action execution, invites, or new renderers.
 */
(function(){
  'use strict';
  if (window.DSDGovernanceRegistry && window.DSDGovernanceRegistry.version) return;

  var VERSION = 'p11dz147-governance-registry-v1';
  var OWNER = 'GovernanceRegistry';
  var FIREBASE_GATE = 'disabled-governance-contract-only';

  function claim(surface, meta){
    try {
      if (window.DSDRegistry && typeof window.DSDRegistry.claim === 'function') {
        window.DSDRegistry.claim(surface, OWNER, Object.assign({ version: VERSION }, meta || {}));
      }
    } catch(e) {}
  }

  function exists(name){ return !!window[name]; }
  function arrayify(value){
    if (Array.isArray(value)) return value;
    if (value === undefined || value === null || value === '') return [];
    return [value];
  }
  function slug(value){
    return String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/^-+|-+$/g,'');
  }
  function unique(values){
    var seen = {};
    return arrayify(values).map(slug).filter(function(v){
      if (!v || seen[v]) return false;
      seen[v] = true;
      return true;
    });
  }

  var governanceScopes = {
    platform: { key: 'platform', rank: 1000, label: 'Platform', override: true },
    multiverse: { key: 'multiverse', rank: 900, label: 'Multiverse', override: true },
    universe: { key: 'universe', rank: 700, label: 'Universe', parent: 'multiverse' },
    galaxy: { key: 'galaxy', rank: 500, label: 'Galaxy', parent: 'universe' },
    event: { key: 'event', rank: 350, label: 'Event', parent: 'galaxy' },
    storyGroup: { key: 'storyGroup', rank: 320, label: 'Story Group', parent: 'galaxy' },
    conversation: { key: 'conversation', rank: 300, label: 'Conversation / Chat', parent: 'galaxy' },
    post: { key: 'post', rank: 250, label: 'Post', parent: 'author-scope' },
    comment: { key: 'comment', rank: 200, label: 'Comment', parent: 'post' },
    reaction: { key: 'reaction', rank: 100, label: 'Reaction', parent: 'post' }
  };

  var authorityRoles = {
    platformOwner: { key: 'platformOwner', rank: 1000, scope: 'platform', label: 'Platform Owner' },
    platformAdmin: { key: 'platformAdmin', rank: 950, scope: 'platform', label: 'Platform Admin' },
    platformModerator: { key: 'platformModerator', rank: 850, scope: 'platform', label: 'Platform Moderator' },
    universeFounder: { key: 'universeFounder', rank: 700, scope: 'universe', label: 'Universe Founder' },
    universeAdmin: { key: 'universeAdmin', rank: 650, scope: 'universe', label: 'Universe Admin' },
    universeModerator: { key: 'universeModerator', rank: 600, scope: 'universe', label: 'Universe Moderator' },
    galaxyFounder: { key: 'galaxyFounder', rank: 500, scope: 'galaxy', label: 'Galaxy Founder' },
    galaxyAdmin: { key: 'galaxyAdmin', rank: 450, scope: 'galaxy', label: 'Galaxy Admin' },
    galaxyModerator: { key: 'galaxyModerator', rank: 400, scope: 'galaxy', label: 'Galaxy Moderator' },
    eventHost: { key: 'eventHost', rank: 350, scope: 'event', label: 'Event Host' },
    eventModerator: { key: 'eventModerator', rank: 325, scope: 'event', label: 'Event Moderator' },
    chatModerator: { key: 'chatModerator', rank: 300, scope: 'conversation', label: 'Chat Moderator' },
    trustedMember: { key: 'trustedMember', rank: 150, scope: 'member', label: 'Trusted Member' },
    member: { key: 'member', rank: 100, scope: 'member', label: 'Member' }
  };

  var governanceActions = {
    inviteMember: { key: 'inviteMember', minRank: 300, auditRequired: true },
    assignModerator: { key: 'assignModerator', minRank: 450, auditRequired: true },
    assignAdmin: { key: 'assignAdmin', minRank: 500, auditRequired: true },
    removeModerator: { key: 'removeModerator', minRank: 450, auditRequired: true },
    removeAdmin: { key: 'removeAdmin', minRank: 500, auditRequired: true },
    transferOwnership: { key: 'transferOwnership', minRank: 500, founderOnly: true, auditRequired: true, platformReviewRecommended: true },
    updateRules: { key: 'updateRules', minRank: 450, auditRequired: true },
    updateBranding: { key: 'updateBranding', minRank: 450, auditRequired: true },
    createGalaxy: { key: 'createGalaxy', minRank: 650, scope: 'universe', auditRequired: true, configurableByUniverse: true },
    approveGalaxy: { key: 'approveGalaxy', minRank: 650, scope: 'universe', auditRequired: true },
    freezeGalaxy: { key: 'freezeGalaxy', minRank: 650, scope: 'universe', auditRequired: true, platformOverride: true },
    freezeUniverse: { key: 'freezeUniverse', minRank: 900, scope: 'platform', auditRequired: true, platformOnly: true },
    removeContent: { key: 'removeContent', minRank: 300, auditRequired: true },
    hideContent: { key: 'hideContent', minRank: 300, auditRequired: true },
    lockContent: { key: 'lockContent', minRank: 400, auditRequired: true },
    muteMember: { key: 'muteMember', minRank: 300, auditRequired: true },
    suspendMemberInScope: { key: 'suspendMemberInScope', minRank: 400, auditRequired: true },
    banUserPlatformWide: { key: 'banUserPlatformWide', minRank: 850, scope: 'platform', platformOnly: true, auditRequired: true },
    emergencyDisableSurface: { key: 'emergencyDisableSurface', minRank: 900, scope: 'platform', platformOnly: true, auditRequired: true },
    reviewReport: { key: 'reviewReport', minRank: 300, auditRequired: true },
    resolveAppeal: { key: 'resolveAppeal', minRank: 850, scope: 'platform', platformOnly: true, auditRequired: true }
  };

  var platformOverrideDoctrine = [
    'Platform governance always overrides universe, galaxy, event, story, chat, post, comment, and reaction governance.',
    'User-created founders/admins/moderators may govern identity, membership, rules, and day-to-day moderation only within their approved scope.',
    'No user-created governance role may override global bans, age/content policy, adult/sensitive restrictions, copyright/takedown policy, trust and safety actions, or emergency platform freezes.',
    'Universe founders can invite members, assign universe admins/moderators, configure galaxy creation policy, approve galaxies, and manage universe identity, but they cannot remove platform controls.',
    'Universe admins can create/approve galaxies and assign galaxy moderators when configured, but cannot remove or demote the universe founder.',
    'Galaxy founders can invite members, assign galaxy admins/moderators, manage galaxy rules/branding/events/stories/chat, but cannot override universe or platform governance.',
    'Galaxy admins and moderators can operate day-to-day moderation only inside the galaxy scope assigned to them.',
    'Every future governance action must produce an audit log record before it is allowed to mutate canonical state.',
    'This registry defines authority only; it does not execute admin actions, moderation actions, Firebase writes, Firebase reads, realtime listeners, or enforcement.'
  ];

  var scopeInheritance = {
    platform: { inheritsFrom: null, canOverride: ['multiverse','universe','galaxy','event','storyGroup','conversation','post','comment','reaction'] },
    multiverse: { inheritsFrom: 'platform', canOverride: ['universe','galaxy','event','storyGroup','conversation'] },
    universe: { inheritsFrom: 'multiverse', canOverride: ['galaxy','event','storyGroup','conversation','post','comment'] },
    galaxy: { inheritsFrom: 'universe', canOverride: ['event','storyGroup','conversation','post','comment'] },
    event: { inheritsFrom: 'galaxy', canOverride: ['conversation','storyGroup','post','comment'] },
    storyGroup: { inheritsFrom: 'galaxy', canOverride: ['post','comment'] },
    conversation: { inheritsFrom: 'galaxy', canOverride: ['comment'] }
  };

  var futurePathContracts = {
    governanceAssignments: 'governance/{scopeType}/{scopeId}/roles/{userId}',
    governanceActions: 'governance/{scopeType}/{scopeId}/actions/{actionId}',
    auditLog: 'auditLogs/{scopeType}/{scopeId}/items/{auditId}',
    platformOverrides: 'platformGovernance/overrides/{overrideId}',
    universeGovernance: 'universes/{universeId}/governance/{recordId}',
    galaxyGovernance: 'galaxies/{galaxyId}/governance/{recordId}',
    userGovernanceIndex: 'users/{userId}/governanceAssignments/{assignmentId}'
  };

  function normalizeRole(role){
    var key = String(role || 'member').trim();
    if (authorityRoles[key]) return key;
    var s = slug(role || 'member');
    var keys = Object.keys(authorityRoles);
    for (var i=0; i<keys.length; i++) if (slug(keys[i]) === s) return keys[i];
    return 'member';
  }
  function normalizeScope(scope){
    var key = slug(scope || 'galaxy');
    if (key === 'chat') key = 'conversation';
    if (key === 'story') key = 'storyGroup';
    return governanceScopes[key] ? key : 'galaxy';
  }
  function normalizeAction(action){
    var key = String(action || '').trim();
    if (governanceActions[key]) return key;
    var s = slug(action || '');
    var keys = Object.keys(governanceActions);
    for (var i=0; i<keys.length; i++) if (slug(keys[i]) === s) return keys[i];
    return null;
  }

  function canPerform(role, action, scope, context){
    role = normalizeRole(role);
    action = normalizeAction(action);
    scope = normalizeScope(scope);
    context = context || {};
    if (!action) return false;
    var roleDef = authorityRoles[role] || authorityRoles.member;
    var actionDef = governanceActions[action];
    if (actionDef.platformOnly && roleDef.scope !== 'platform') return false;
    if (actionDef.founderOnly && role.indexOf('Founder') === -1 && role !== 'platformOwner') return false;
    if (roleDef.rank < actionDef.minRank) return false;
    if (context.targetRole && authorityRoles[normalizeRole(context.targetRole)] && roleDef.rank <= authorityRoles[normalizeRole(context.targetRole)].rank) return false;
    return true;
  }

  function normalizeAssignment(input, context){
    input = input || {};
    context = context || {};
    var scopeType = normalizeScope(input.scopeType || input.entityType || context.scopeType);
    var role = normalizeRole(input.role || context.role);
    var scopeId = input.scopeId || input.entityId || input.universeId || input.galaxyId || context.scopeId || null;
    var userId = input.userId || input.uid || context.userId || null;
    return {
      version: VERSION,
      assignmentId: input.assignmentId || [scopeType, scopeId || 'pending-scope', userId || 'pending-user', role].join(':'),
      userId: userId,
      scopeType: scopeType,
      scopeId: scopeId,
      role: role,
      authorityRank: (authorityRoles[role] || authorityRoles.member).rank,
      assignedBy: input.assignedBy || context.assignedBy || null,
      state: input.state || 'active',
      inheritedScopes: unique([].concat(input.inheritedScopes || [], context.inheritedScopes || [])),
      firebaseReadsEnabled: false,
      firebaseWritesEnabled: false,
      realtimeEnabled: false,
      enforcementMode: 'governance-contract-only'
    };
  }

  function getReport(reason){
    return {
      version: VERSION,
      reason: reason || 'manual-report',
      owner: OWNER,
      firebaseGate: FIREBASE_GATE,
      firebaseReadsEnabled: false,
      firebaseWritesEnabled: false,
      realtimeEnabled: false,
      enforcementEnabled: false,
      adminActionsEnabled: false,
      moderationActionsEnabled: false,
      auditLogWritesEnabled: false,
      doctrine: platformOverrideDoctrine,
      governanceScopes: governanceScopes,
      authorityRoles: authorityRoles,
      governanceActions: governanceActions,
      scopeInheritance: scopeInheritance,
      pathContracts: futurePathContracts,
      dependencyReadiness: {
        DSDRegistry: exists('DSDRegistry'),
        DSDMembershipRegistry: exists('DSDMembershipRegistry'),
        DSDModerationMetadata: exists('DSDModerationMetadata'),
        DSDVisibilityLayer: exists('DSDVisibilityLayer'),
        DSDClassificationLayer: exists('DSDClassificationLayer'),
        DSDFirebaseSocialSchema: exists('DSDFirebaseSocialSchema'),
        DSDRealtimeOwnershipAudit: exists('DSDRealtimeOwnershipAudit'),
        firebaseReadsEnabled: false,
        firebaseWritesEnabled: false,
        realtimeEnabled: false
      },
      nextGate: 'p11dz148-identity-registry-or-controlled-read-only-post-activation-gate'
    };
  }

  function publish(reason){
    var report = getReport(reason || 'publish');
    claim('governance-registry:doctrine', { doctrine: platformOverrideDoctrine, count: platformOverrideDoctrine.length });
    claim('governance-registry:scopes', { governanceScopes: governanceScopes, count: Object.keys(governanceScopes).length });
    claim('governance-registry:authority-roles', { authorityRoles: authorityRoles, count: Object.keys(authorityRoles).length });
    claim('governance-registry:actions', { governanceActions: governanceActions, count: Object.keys(governanceActions).length });
    claim('governance-registry:scope-inheritance', scopeInheritance);
    claim('governance-registry:path-contracts', futurePathContracts);
    claim('governance-registry:dependency-readiness', report.dependencyReadiness);
    claim('governance-registry:future-ecosystem-readiness', {
      multiverseGovernance: true,
      universeGovernance: true,
      galaxyGovernance: true,
      eventGovernance: true,
      galaxyStoriesGovernance: true,
      galaxyChatGovernance: true,
      auditLogRequiredBeforeMutation: true
    });
    claim('governance-registry:firebase-firewall', {
      firebaseReadsEnabled: false,
      firebaseWritesEnabled: false,
      realtimeEnabled: false,
      enforcementEnabled: false,
      adminActionsEnabled: false,
      moderationActionsEnabled: false,
      auditLogWritesEnabled: false,
      gate: FIREBASE_GATE
    });
    try { window.dispatchEvent(new CustomEvent('dsd:governance-registry-ready', { detail: report })); } catch(e) {}
    window.DSDGovernanceRegistryReport = report;
    return report;
  }

  window.DSDGovernanceRegistry = {
    version: VERSION,
    owner: OWNER,
    getReport: getReport,
    publish: publish,
    governanceScopes: governanceScopes,
    authorityRoles: authorityRoles,
    governanceActions: governanceActions,
    scopeInheritance: scopeInheritance,
    normalizeAssignment: normalizeAssignment,
    canPerform: canPerform,
    normalizeRole: normalizeRole,
    normalizeScope: normalizeScope,
    normalizeAction: normalizeAction
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function(){ publish('dom-ready'); }, { once: true });
  } else {
    publish('already-ready');
  }
})();
